import { getTradingOrderDetailsRoute } from '@src/containers/Routes/routes';
import type { GenerateOrderDetailsRoute } from '@src/containers/Trading/Markets/OrderDetails/types';
import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers';
import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  DEFAULT_MAX_ROWS,
  EXECUTION_REPORT,
  ExecTypeEnum,
  ExecutionReport,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  WSBlotterTableMaxRecordsReachedWarning,
  filterByColumnMainMenuItems,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useExecutionReportColumns,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type BlotterTableSort,
  type Column,
} from '@talos/kyoko';
import type { GetMainMenuItemsParams } from 'ag-grid-community';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../hooks';
import { useExecutionReportMenu } from './ExecutionReportMenu';
import {
  colIDToFilterBuilderKey,
  onlyServerFilterKeys,
  useExecutionReportFilter,
  type ExecutionReportsTableFilter,
} from './useExecutionReportFilter';

const DEFAULT_SORT: BlotterTableSort<ExecutionReport> = ['-Timestamp', '-Revision'];

export interface ExecutionReportsBlotterTableProps {
  /** Unique id per blotter instance */
  blotterID: string;
  /** ID for common blotter type persistence; usually a subset of the blotterId to represent blotter area 'type' */
  blotterPersistID?: string;
  tabLabel?: string;
  orderID?: string;
  rfqID?: string;
  parentOrderID?: string;
  parentRFQID?: string;

  defaultColumns?: (Column | keyof ExecutionReport)[];
  defaultFilter?: ExecutionReportsTableFilter;
  defaultSort?: BlotterTableSort<ExecutionReport>;
  persistFilter?: boolean;
  persistSort?: boolean;

  initialIsOpen?: boolean;

  onCloneTab?: () => void;
  generateOrderDetailsRoute?: GenerateOrderDetailsRoute;

  /** Layout ID, currently only used for opening order details in the trading layout */
  layoutId: string | undefined;
}

export function ExecutionReportsBlotterTable({
  tabLabel,
  orderID,
  rfqID,
  parentOrderID,
  parentRFQID,
  blotterID,
  blotterPersistID = blotterID,
  defaultColumns,
  defaultFilter,
  defaultSort = DEFAULT_SORT,
  persistFilter,
  initialIsOpen,
  onCloneTab,
  generateOrderDetailsRoute = getTradingOrderDetailsRoute,
  layoutId,
}: ExecutionReportsBlotterTableProps) {
  const { getConfig } = useOrgConfiguration();
  const mixpanel = useMixpanel();

  const columns = useExecutionReportColumns({ defaultColumns });

  const persistedBlotterTable = usePersistedBlotterTable(
    blotterPersistID,
    { filter: defaultFilter },
    { persistFilter }
  );

  const filterResults = useExecutionReportFilter({
    persistedBlotterTable,
    includeStatusFilter: true,
    includeIDFilter: orderID == null && rfqID == null,
  });
  const { clientSideFilter: clientLocalFilter, blotterTableFilterProps, filterBuilderProps } = filterResults;

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const {
    getCustomContextMenuItems,
    columns: extraColumns,
    dialogs,
  } = useExecutionReportMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterBuilderProps.properties,
    generateOrderDetailsRoute,
    layoutId,
  });
  const getExtraMainMenuItems = useCallbackRef((params: GetMainMenuItemsParams) => {
    return filterByColumnMainMenuItems({
      params,
      colIDToFilterBuilderKey,
      openClause: filterBuilderAccordion.openClause,
      mixpanel,
    });
  });

  const columnsWithMenu = useMemo(() => [...columns, ...extraColumns], [columns, extraColumns]);

  const { showBlotterPauseButton } = useFeatureFlag();

  const request = useMemo(
    () => ({
      name: EXECUTION_REPORT,
      tag: blotterID,
      ...(orderID != null && { OrderID: orderID }),
      ...(rfqID != null && { RFQID: rfqID }),
      ...(parentOrderID != null && { ParentOrderID: parentOrderID }),
      ...(parentRFQID != null && { ParentRFQID: parentRFQID }),
      SendMarkets: true,
      CoalesceExecs: [ExecTypeEnum.Restated, ExecTypeEnum.Trade],
      ...removeEmptyFilters(onlyServerFilterKeys(filterResults.filter)),
    }),
    [blotterID, orderID, rfqID, parentOrderID, parentRFQID, filterResults.filter]
  );

  const blotterTable = useWsBlotterTable({
    request,
    persistence: persistedBlotterTable,
    rowID: ExecutionReport.rowID,
    clientLocalFilter,
    startingRowLimit: getConfig(OrgConfigurationKey.BlotterRowsMax, DEFAULT_MAX_ROWS),
    columns: columnsWithMenu,
    getExtraMainMenuItems,

    pauseParams: {
      showPauseButton: showBlotterPauseButton,
    },
    contextMenuOptions: {
      getCustomContextMenuItems,
    },
    gridOptions: {
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
    },
    exportOptions: {
      name: 'ExecutionReports',
      tabLabel: tabLabel,
    },
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={r => r.Timestamp} />
      <BlotterTable {...blotterTable} />
      {dialogs}
    </>
  );
}
